/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from 'react';
import MediaPrepPageHeader from '../../../../common/commonComponents/mediaPrepPageHeader';
import MediaPrepHeaderSection from '../../../../common/commonComponents/mediaPrepheaderSection';
import { useTranslation } from 'react-i18next';
import { MediaSolutionService } from '../../../../../service/MediaSolutionServices';
import SolutionInventory from '..';


interface SolutionLotDetailsProps {
  lotId: string;
  stockStatus?: string;
  batchName?: string;
  mediaName?: string;
  onBack: () => void;
}

const SolutionLotDetails: React.FC<SolutionLotDetailsProps> = ({ lotId, stockStatus, batchName, mediaName, onBack }) => {

  const [lotList, setLotList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);  
  const [selectedLot, setSelectedLot] = useState<any>(null);

  useEffect(() => {
    const fetchLotDetails = async () => {
        setLoading(true);
        try {
        const res = await MediaSolutionService.getMediaSolutionLotDetails(lotId);

        if (res?.status) {
            setLotList(res.data || []);
        } else {
            setLotList([]);
        }
        } catch (err) {
        console.error("Error fetching solution lot details", err);
        } finally {
        setLoading(false);
        }
    };

    if (lotId) fetchLotDetails();
  }, [lotId]);

  const distributionData = lotList.map((item: any) => ({
    name: item.lot_code,
    stockLevel: Number(item.stock_level_percentage || 0),
    availableStock: parseFloat(item.available_stock?.split(" ")[0] || 0),
    unit: item.available_stock?.split(" ")[1] || "UNIT",
    firstOpenedDate: item.opened_date === "NOT OPENED" ? null : item.opened_date,
    shelfLife: item.remaining_shelf_life || "-", 
    shelfLifeStatus: item.opened_date === "NOT OPENED" ? "NOT OPENED" : "ACTIVE",
    shelfLifeStatusColor:
        item.opened_date === "NOT OPENED"
        ? "text-slate-400"
        : "text-emerald-500",
    }));

    const getStatusStyle = (status?: string) => {
    switch (status) {
        case "Released":
        return "bg-emerald-100 text-emerald-700 border-emerald-200";
        case "Quarantine":
        return "bg-purple-100 text-purple-700 border-purple-200";
        case "Rejected":
        return "bg-red-100 text-red-700 border-red-200";
        default:
        return "bg-slate-100 text-slate-500 border-slate-200";
    }
    };


  const { t } = useTranslation();

  return (
    <div className="media-prep-scope min-h-screen bg-[#F8FAFC] text-slate-900 pb-20">
    <MediaPrepHeaderSection />
      <MediaPrepPageHeader pageHeader={t('solutionInventory.lotDetails.pageTitle')} />
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-orange-500 hover:bg-orange-50 transition-all">
            <span className="material-symbols-outlined text-xl">{t('mediaPowder.icons.arrow_back')}</span>
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-black text-slate-800 tracking-tight uppercase flex items-center gap-3">
                {t('solutionInventory.lotDetails.pageTitle')}:
                <span className="px-3 py-1.5 bg-slate-100 text-slate-900 font-mono font-black text-lg rounded-lg border border-slate-200 shadow-sm">
                  {batchName}
                </span>
              </h1>
              <span className="px-2.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] font-black uppercase rounded-full border border-emerald-200">
                {stockStatus}
              </span>
            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              {mediaName}.{lotList[0]?.manufacturer_name}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button className="px-4 py-2 bg-[#2D3E50] text-white rounded-lg font-black text-[10px] uppercase tracking-widest hover:brightness-110 shadow-sm transition-all">
            {t('solutionInventory.lotDetails.printBatchLabel')}
          </button>
        </div>
      </div>

      <div className="p-12">
        {/* Detailed Distribution Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-8 py-6 flex justify-between items-center border-b border-slate-50 bg-[#FBFDFE]">
            <div className="flex items-center gap-3">
              <span className="material-symbols-outlined text-orange-500 font-bold">{t('solutionInventory.lotDetails.list_alt')}</span>
              <h3 className="text-base font-black text-[#2D3E50] tracking-tight">{t('solutionInventory.lotDetails.tableHeader')}</h3>
            </div>
            <span className="text-[11px] font-bold text-slate-300 italic">Last Usage: Today 09:30 AM</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-[#F8FAFC] text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3">{t('solutionInventory.lotDetails.UnitIdentifier')}</th>
                  <th className="px-4 py-3">{t('solutionInventory.lotDetails.VolumeLevel')}</th>
                  <th className="px-4 py-3">{t('solutionInventory.lotDetails.AvailableVolume')}</th>
                  <th className="px-4 py-3">{t('solutionInventory.lotDetails.FirstOpenedDate')}</th>
                  <th className="px-4 py-3 text-right">{t('solutionInventory.lotDetails.RemainingLife')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {distributionData.map((lot, i) => (
                  <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-9 py-8">
                      <span className="text-base font-black text-[#2D3E50] tracking-tight">{lot.name}</span>
                    </td>
                    <td className="px-9 py-8">
                      <div className="flex items-center gap-4 w-64">
                        <div className="flex-1 h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-[#FF7043] rounded-full transition-all duration-1000"
                            style={{ width: `${lot.stockLevel}%` }}
                          ></div>
                        </div>
                        <span className="text-[11px] font-black text-orange-400 tracking-widest">{lot.stockLevel}%</span>
                      </div>
                    </td>
                    <td className="px-9 py-8">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-2xl font-black ${lot.availableStock < 50 ? 'text-red-500' : 'text-[#2D3E50]'}`}>
                          {lot.availableStock}
                        </span>
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{lot.unit}</span>
                      </div>
                    </td>
                    <td className="px-9 py-8">
                      {lot.firstOpenedDate ? (
                        <div className="flex items-center gap-3 px-4 py-2 bg-white border border-slate-200 rounded-lg w-fit shadow-sm">
                          <span className="material-symbols-outlined text-orange-500 text-sm">{t('mediaPowder.icons.calendar_today')}</span>
                          <span className="text-[11px] font-black text-slate-700">{lot.firstOpenedDate}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-3 px-4 py-2 bg-slate-50 border border-slate-100 rounded-lg w-fit">
                          <span className="material-symbols-outlined text-slate-300 text-sm">{t('solutionInventory.icons.lock')}</span>
                          <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{t('solutionInventory.lotDetails.NotOpened')}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-9 py-8 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-sm font-black text-slate-700 tracking-tight">{lot.shelfLife}</span>
                        {/* <span className={`text-[9px] font-black uppercase tracking-widest ${lot.shelfLifeStatusColor}`}>
                          {lot.shelfLifeStatus}
                        </span> */}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons Footer */}
        <div className="mt-12 flex justify-between items-center">
          <button 
            onClick={onBack}
            className="flex items-center gap-3 px-8 py-3.5 text-[11px] font-black text-slate-500 uppercase tracking-widest border border-slate-200 rounded-xl hover:bg-slate-50 transition-all"
          >
            <span className="material-symbols-outlined text-sm">{t('mediaPowder.icons.arrow_back')}</span>
            {t('solutionInventory.lotDetails.BackToInventory')}
          </button>
          <div className="flex gap-4">
             <button className="px-8 py-3.5 bg-[#2D3E50] text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:brightness-110 shadow-lg shadow-slate-200 transition-all flex items-center gap-3">
               <span className="material-symbols-outlined text-sm">{t('solutionInventory.icons.print')}</span>
               {t('solutionInventory.lotDetails.PrintUnitLabels')}
             </button>
             <button className="px-8 py-3.5 bg-[#FF7043] text-white rounded-xl text-[11px] font-black uppercase tracking-widest hover:brightness-110 shadow-lg shadow-orange-500/20 transition-all flex items-center gap-3">
               <span className="material-symbols-outlined text-sm">{t('mediaPowder.icons.history')}</span>
               {t('solutionInventory.lotDetails.ViewAuditTrail')}
             </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolutionLotDetails;
